import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Calendar, Palette, Wand2, Users, MapPin, CalendarClock, Server,
  Award, Sparkles, Gift, Trophy, Radio, ImageIcon, Loader2,
  CheckCircle2, Circle, AlertCircle, ChevronRight, UserPlus, ShoppingBag,
  Zap, BookOpen,
} from 'lucide-react'
import { adminAPI } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import PageHeader from '@/components/PageHeader'

/* Setup-hub — pre-LAN landings-pagina.
   Visuele checklist per sectie. Elk item toont wat er al staat en linkt
   naar de plek waar je het configureert. Doel: nieuwe admin heeft één
   startpunt om een LAN op te zetten zonder rondzoeken. */

function StatusIcon({ status }) {
  if (status === 'done')    return <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
  if (status === 'partial') return <AlertCircle   className="h-4 w-4 text-peach shrink-0" />
  return <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />
}

function ChecklistRij({ status, label, detail, to, external }) {
  const inner = (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted/50 border border-transparent hover:border-border transition-colors">
      <StatusIcon status={status} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {detail && <div className="text-xs text-muted-foreground">{detail}</div>}
      </div>
      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    </div>
  )
  if (external) return <a href={to} target="_blank" rel="noopener noreferrer" className="block no-underline">{inner}</a>
  return <Link to={to} className="block no-underline">{inner}</Link>
}

function Sectie({ icon: Icon, titel, kleur, children }) {
  // Nummering weggehaald — de secties zijn géén verplichte sequentie
  // (branding vóór modules is niet dwingend). Volgorde blijft logisch.
  return (
    <Card style={{ borderLeft: `3px solid ${kleur}` }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon size={16} style={{ color: kleur }} />
          {titel}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-0.5">{children}</CardContent>
    </Card>
  )
}

/* Compacte tegel-variant voor sectie "Snelle links" — geen status-vinkje,
   geen detail-tekst, dichter opeen. Referentie-links horen visueel niet
   gelijk-gewichtig aan de checklist-items zelf. */
function SnelTegel({ label, to, external, icon: Icon }) {
  const inner = (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border hover:border-primary/40 text-sm transition-colors">
      {Icon && <Icon size={14} className="text-muted-foreground shrink-0" />}
      <span className="truncate flex-1">{label}</span>
    </div>
  )
  if (external) return <a href={to} target="_blank" rel="noopener noreferrer" className="no-underline">{inner}</a>
  return <Link to={to} className="no-underline">{inner}</Link>
}

export default function SetupHubPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adminAPI.getSetupStatus()
      .then(r => setData(r.data))
      .catch(e => console.warn('setup-status:', e.message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="p-6"><Loader2 className="animate-spin" /></div>
  if (!data)   return <div className="p-6 text-signal">Kon setup-status niet laden.</div>

  const s = data.sections
  const ed = data.active_edition

  // Helpers voor status-per-rij: done als count > 0 of bool true, partial als "iets maar niet compleet".
  const st = (bool) => bool ? 'done' : 'todo'
  const stCount = (n) => n > 0 ? (n > 1 ? 'done' : 'partial') : 'todo'

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <PageHeader
        icon={Wand2}
        title="LAN opzetten"
        subtitle="Loop deze checklist door voordat de deelnemers arriveren"
        actions={ed && (
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Actief</span>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/lan-edities">{ed.name}{ed.edition ? ` — ${ed.edition}` : ''} <ChevronRight className="h-3 w-3 ml-1" /></Link>
            </Button>
          </div>
        )}
      />

      {!ed && (
        <Card style={{ borderLeft: '3px solid #FFB57A' }}>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-peach shrink-0" />
            <div className="flex-1 text-sm">
              Nog geen actieve LAN-editie. <strong>Start met de wizard</strong> om je LAN in enkele stappen op te zetten.
            </div>
            <Button asChild>
              <Link to="/admin/lan-wizard"><Wand2 className="h-4 w-4 mr-1" />Nieuwe LAN wizard</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <Sectie icon={Calendar} titel="LAN-basis" kleur="#7BC8FF">
        <ChecklistRij status={st(s.basis.editie_actief)}
          label="LAN-editie"
          detail={ed ? `Actief: ${ed.name}${ed.edition ? ` — ${ed.edition}` : ''}` : 'Nog geen editie aangemaakt'}
          to="/admin/lan-edities" />
        <ChecklistRij status={st(s.basis.branding_gezet)}
          label="Branding"
          detail={s.basis.branding_gezet ? 'Site-naam en logo ingevuld' : 'Nog geen eigen branding'}
          to="/admin/branding" />
        <ChecklistRij status={stCount(s.basis.modules_actief)}
          label="Modules"
          detail={`${s.basis.modules_actief} van ${s.basis.modules_totaal} modules actief`}
          to="/admin?section=setup&tab=modules" />
      </Sectie>

      <Sectie icon={Users} titel="Deelnemers" kleur="#B4FF39">
        <ChecklistRij status={stCount(s.deelnemers.users_actief)}
          label="Users importeren"
          detail={`${s.deelnemers.users_actief} actieve users. Importeer nieuwe via de kassa-lijst.`}
          to="/admin/deelnemers-import" />
        <ChecklistRij status={stCount(s.deelnemers.seat_layouts)}
          label="Zetel-mapping"
          detail={s.deelnemers.seat_layouts > 0
            ? `${s.deelnemers.seat_layouts} layout(s) · ${s.deelnemers.zetels_toegewezen} zetels toegewezen`
            : 'Nog geen zetel-layout'}
          to="/seats" />
      </Sectie>

      <Sectie icon={CalendarClock} titel="Programma & inhoud" kleur="#FFB57A">
        <ChecklistRij status={stCount(s.programma.slots)}
          label="Programma-slots"
          detail={`${s.programma.slots} slots ingepland`}
          to="/programma/admin" />
        <ChecklistRij status={stCount(s.programma.game_servers_online)}
          label="Game servers"
          detail={`${s.programma.game_servers_online} van ${s.programma.game_servers_totaal} online`}
          to="/game-servers" />
      </Sectie>

      <Sectie icon={Award} titel="Belonings-catalogus" kleur="#E4C265">
        <ChecklistRij status={stCount(s.belonings.badges)}
          label="Badges"
          detail={`${s.belonings.badges} badge-types beschikbaar`}
          to="/admin/badges" />
        <ChecklistRij status={stCount(s.belonings.prijzen)}
          label="Prijzen (prijzenkraam)"
          detail={`${s.belonings.prijzen} prijzen in ${s.belonings.prize_categorieen} categorieën`}
          to="/prijzenkraam/admin" />
        <ChecklistRij status={stCount(s.belonings.profielitems)}
          label="Profielitems"
          detail={`${s.belonings.profielitems} items (banners, frames, accents)`}
          to="/admin/profile-items" />
      </Sectie>

      <Sectie icon={Trophy} titel="Compo's klaarzetten" kleur="#C77BFF">
        <ChecklistRij status={stCount(s.compos.toernooien)}
          label="Toernooien"
          detail={`${s.compos.toernooien} toernooi(en) aangemaakt`}
          to="/tournaments" />
        <ChecklistRij status={stCount(s.compos.toernooi_templates)}
          label="Toernooi-templates"
          detail={`${s.compos.toernooi_templates} templates om snel een toernooi te starten`}
          to="/tournaments/templates" />
        <ChecklistRij status={s.compos.kempenvision_show_actief ? 'done' : 'todo'}
          label="Kempenvision-show"
          detail={s.compos.kempenvision_show_actief
            ? `Actief: ${s.compos.kempenvision_show_actief.name}`
            : 'Geen actieve show'}
          to="/kempenvision/admin" />
        <ChecklistRij status={s.compos.imagecompo_show_actief ? 'done' : 'todo'}
          label="Image Compo-show"
          detail={s.compos.imagecompo_show_actief
            ? `Actief: ${s.compos.imagecompo_show_actief.name}`
            : 'Geen actieve show'}
          to="/imagecompo/admin" />
      </Sectie>

      {/* Sectie "Snelle links" is bewust lichter dan de checklist-secties —
          referentie, geen todo. Compacte grid, geen chevrons, geen status-vinkjes. */}
      <div className="pt-2">
        <div className="eyebrow mb-2 px-1">Snelle links (setup)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <SnelTegel icon={Calendar}   label="LAN-edities"          to="/admin/lan-edities" />
          <SnelTegel icon={UserPlus}   label="Deelnemers importeren" to="/admin/deelnemers-import" />
          <SnelTegel icon={Palette}    label="Branding"             to="/admin/branding" />
          <SnelTegel icon={Award}      label="Badges beheren"       to="/admin/badges" />
          <SnelTegel icon={Sparkles}   label="Profielitems"         to="/admin/profile-items" />
          <SnelTegel icon={Gift}       label="Prijzenkraam admin"   to="/prijzenkraam/admin" />
          <SnelTegel icon={Wand2}      label="Instellingen (admin)" to="/admin?section=setup" />
          <SnelTegel icon={ShoppingBag} label="Kassa admin (extern)" external
            to={typeof window !== 'undefined' && window.location.hostname.endsWith('.ts.net')
              ? 'https://clubkassa.tail9a4b92.ts.net/admin'
              : 'http://clubkassa.local:5000/admin'} />
          <SnelTegel icon={Zap}       label="Streamdeck-config" to="/admin/streamdeck" />
          <SnelTegel icon={Radio}     label="Companion web-UI (extern)" external
            to={typeof window !== 'undefined' && window.location.hostname.endsWith('.ts.net')
              ? 'https://companion.tail9a4b92.ts.net'
              : (typeof window !== 'undefined'
                  ? `${window.location.protocol}//${window.location.hostname}:8000`
                  : '')} />
          <SnelTegel icon={BookOpen}  label="Streamdeck-wiki" to="/wiki/streamdeck-integratie" />
        </div>
      </div>

      <div className="text-xs text-muted-foreground text-center pt-4 border-t border-border/40">
        Tijdens de LAN zelf gebruik je <Link to="/regie" className="text-acid underline">Regie</Link>{' '}
        voor live-monitoring — deze setup-hub is voor voorbereiding.
      </div>
    </div>
  )
}
